import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["column", "bodyTable"]
  static values = { storageKey: String }

  connect() {
    this.resizing = false
    this.loadFromStorage()
    this.setupResizers()
    this.applyStoredWidths()

    // Apply widths immediately on mutations
    this.observer = new MutationObserver(() => {
      this.applyStoredWidths()
    })
    this.observer.observe(this.element, {
      childList: true,
      subtree: true
    })
  }

  get storageKey() {
    return this.hasStorageKeyValue ? this.storageKeyValue : 'resizable-table-widths'
  }

  loadFromStorage() {
    try {
      const stored = localStorage.getItem(this.storageKey)
      if (!stored) return

      const widths = JSON.parse(stored)
      this.columnTargets.forEach((column) => {
        const colName = column.textContent.trim()
        if (widths[colName]) {
          column.dataset.width = widths[colName]
        }
      })
    } catch (e) {
      console.warn("Failed to load column widths:", e)
    }
  }

  saveToStorage() {
    try {
      const widths = {}
      this.columnTargets.forEach((column) => {
        const colName = column.textContent.trim()
        if (column.dataset.width) {
          widths[colName] = column.dataset.width
        }
      })
      localStorage.setItem(this.storageKey, JSON.stringify(widths))
    } catch (e) {
      console.warn("Failed to save column widths:", e)
    }
  }

  setupResizers() {
    this.columnTargets.forEach((column) => {
      const resizer = column.querySelector('.column-resizer')
      if (!resizer) return

      // Get actual column index from DOM position
      const actualIndex = Array.from(column.parentElement.children).indexOf(column)

      resizer.addEventListener('mousedown', (e) => {
        this.startResize(e, actualIndex, column)
      })
    })
  }

  startResize(event, columnIndex, column) {
    this.resizing = true
    this.currentColumnIndex = columnIndex
    this.initialX = event.pageX
    this.initialWidth = column.offsetWidth
    
    document.body.style.cursor = 'col-resize'
    document.addEventListener('mousemove', this.resize)
    document.addEventListener('mouseup', this.stopResize)
    
    event.preventDefault()
  }

  resize = (event) => {
    if (!this.resizing) return
    
    const diff = event.pageX - this.initialX
    const newWidth = Math.max(50, this.initialWidth + diff)
    
    this.updateColumnWidth(this.currentColumnIndex, newWidth, false)
    this.storeColumnWidth(this.currentColumnIndex, newWidth)
  }

  updateColumnWidth(columnIndex, width, animate = false) {
    const selector = `th:nth-child(${columnIndex + 1}), td:nth-child(${columnIndex + 1})`
    const cells = this.element.querySelectorAll(selector)
    
    cells.forEach(cell => {
      cell.style.transition = animate ? 'width 50ms ease-out' : 'none'
      cell.style.width = `${width}px`
      cell.style.minWidth = `${width}px`
      cell.style.maxWidth = `${width}px`
    })
  }

  storeColumnWidth(columnIndex, width) {
    const selector = `th:nth-child(${columnIndex + 1})`
    const th = this.element.querySelector(selector)
    if (th) th.dataset.width = width
  }

  applyStoredWidths() {
    this.columnTargets.forEach((column) => {
      const storedWidth = column.dataset.width
      if (storedWidth) {
        const actualIndex = Array.from(column.parentElement.children).indexOf(column)
        this.updateColumnWidth(actualIndex, parseInt(storedWidth), true)
      }
    })
  }

  // Turbo frame updates listener
  initialize() {
    let timeout
    const observer = new MutationObserver(() => {
      clearTimeout(timeout)
      timeout = setTimeout(() => this.applyStoredWidths(), 50)
    })

    observer.observe(this.element, {
      childList: true,
      subtree: true
    })
  }

  stopResize = () => {
    this.resizing = false
    document.body.style.cursor = ''
    document.removeEventListener('mousemove', this.resize)
    document.removeEventListener('mouseup', this.stopResize)
    this.saveToStorage()
  }

  disconnect() {
    this.stopResize()
    this.observer?.disconnect()
  }

  syncHeaderScroll(event) {
    const headerContainer = this.element.querySelector('.overflow-x-auto')
    headerContainer.scrollLeft = event.target.scrollLeft
  }
}
